import { Injectable } from "@angular/core";
import { AuthorizationService } from "./authorization.service";
import { Location } from "@angular/common";
import { Router } from "@angular/router";
import { AngularAppLogger } from "../angular-app-logger";
import { v4 as uuidv4 } from "uuid";
import { AppConfig } from "../shared/constants/app.config";
import {AppStateService} from "../shared/service/app.state.service";
import {environment} from "../../environments/environment";
import {firstValueFrom} from "rxjs";
import {HttpClient} from "@angular/common/http";
import {ClockSkewService} from "./clock-skew.service";

@Injectable()
export class AppInitService {
  constructor(
    readonly authorizationService: AuthorizationService,
    readonly location: Location,
    readonly appLogger: AngularAppLogger,
    readonly window: Window,
    private router: Router,
    readonly appState: AppStateService,
    readonly http: HttpClient,
    readonly clockSkewService: ClockSkewService
  ) { }
  async initializeApp(): Promise<any> {

    try {
      // Check clock-skew and determine if app launch possible
      if (await this.clockSkewService.isClockSkewed())
      {
        window.alert(
          'Unable to launch application.\n\n' +
          'Your computer date and time are out of sync with the authentication server. ' +
          'Please correct your system clock and reopen the application.'
        );
        window.close();
        return;
      }

      // Check if session is new using a new key - then ensure localStorage gets reset on a few items - instead of a full clear
      if (!sessionStorage.getItem('activeSession') && !this.isOidcCallback()) {
        this.appState.setEhrAccessToken(null);
        localStorage.removeItem('patient');
        localStorage.removeItem('authorizationState');
        localStorage.removeItem('authorizationToken');
      }
      sessionStorage.setItem('activeSession', 'true');

      if (!this.window.location.href.includes('?')) {
        console.log(
          'Location doesn\'t seem to have query paremeters..',
          this.window.location.href
        );
      } else {

        // Skip EHR processing if this is an OIDC callback
        if (this.isOidcCallback()) {
          console.log('OIDC callback detected, skipping EHR processing');
          return null;
        }

        // Get current URL parameters
        const currentToken = this.authorizationService.getEhrCode(this.window.location.href);
        const currentState = this.authorizationService.getEhrState(this.window.location.href);

        // Always use current URL parameters for fresh launch
        let authorizationToken = currentToken;
        let authorizationState = currentState;

        if (authorizationToken !== null) {
          const response = await this.authorizationService
            .codeToBearerToken(
              localStorage.getItem("tokenUrl") ?? '',
              authorizationToken,
              authorizationState,
              localStorage.getItem("validCodeState")
            );
          if (response && response["patient"]) {
            await this.setAccessTokenAndPatientInfo(response);
          } else {
            this.setGlobalErrorAndHandleError(response || {});
          }
        }
        const iss = this.authorizationService.getIss(this.window.location.href),
          launchToken = this.authorizationService.getLaunchToken(
            this.window.location.href
          );

        if (iss && launchToken) {
          await this.setIssAndMetaDataValues(iss, launchToken);
        }
      }
    } catch (error) {
      console.log(error);
    }
    return null;
  }

  private async setIssAndMetaDataValues(iss: string, launchToken: any) {
    localStorage.setItem("iss", iss);

    // Set client ID based on EHR type
    if (iss.includes(AppConfig.LOGICA_EHRS[0])) {
      localStorage.setItem("ehr_client_id", AppConfig.LOGICAL_CLIENT_IDS[0]);
    } else if (iss.includes(AppConfig.LOGICA_EHRS[1])) {
      localStorage.setItem("ehr_client_id", AppConfig.LOGICAL_CLIENT_IDS[1]);
    } else {
      localStorage.setItem("ehr_client_id", AppConfig.EHR_CLIENT_ID);
    }
    try {
      const response1 = await this.authorizationService.getMetadata(iss);
      const validCodeState = `ehr_${uuidv4()}`;
      localStorage.setItem("validCodeState", validCodeState);

      const cibmtrUrl = this.authorizationService.getCibmtrFhirUrl(response1);
      const fhirVersion = this.authorizationService.getFhirVersion(response1);

      localStorage.setItem("cibmtr_url", cibmtrUrl);
      localStorage.setItem("fhir_version", fhirVersion);

      const tokenUrl = this.authorizationService.getTokenUrl(response1);
      const authorizeUrl = this.authorizationService.getAuthorizeUrl(response1);
      const authorizationCodeUrl = this.authorizationService.constructAuthorizationUrl(
        authorizeUrl,
        launchToken,
        iss,
        validCodeState
      );

      localStorage.setItem("tokenUrl", tokenUrl);
      this.window.location.href = authorizationCodeUrl;

      this.appLogger.logInfo("EHR Init: Metadata retrieved. FHIR version = " + fhirVersion,
        {component: "AppInitService", context: "setIssAndMetaDataValues-success"});
    } catch (error) {
      this.appLogger.logError('EHR Init: Error processing metadata', {
        component: "AppInitService",
        context: "setIssAndMetaDataValues-error",
        decodedValue: error
      });
    }
  }

  async setAccessTokenAndPatientInfo(response: any) {
    this.appState.setEhrAccessToken(response["access_token"] ?? null);
    console.log("writing accesstokenandmetadata");
    sessionStorage.setItem("smart_patient_buffer", response["patient"]);
    // localStorage.setItem("patient", response["patient"]);
    // this.appState.setPatient(response["patient"]);

    this.appLogger.logInfo("EHR Init: EHR token exchange successful", {
      context: "setAccessTokenAndPatientInfo-success",
      component: "AppInitService"
    });

    // go to the OrgGateComp
    return await this.router.navigate(['/organization']);
  }

  setGlobalErrorAndHandleError(response: Object) {
    this.appLogger.logError(response, { context: 'Error during app initialization' });

    const iss = localStorage.getItem('iss') ?? 'unknown issuer';
    const errorMessage =
      `Unable to launch the CIBMTR Reporting app. ` +
      `The patient identifier received for ISS "${iss}" is missing.`;

    this.appLogger.logError(errorMessage, {
      context: 'Patient ID not found',
      url: iss,
      allowDuplicate: false
    });

    this.appLogger.logError(errorMessage, {
      context: 'AppInitService.setGlobalErrorAndHandleError',
    });
  }

  handleError(error: any) {
    const errorMessage =
      `Unable to process due to ${error.reason}`;
    this.appLogger.showGlobalAlert(errorMessage, {url: `${error.url}`});
    console.log(errorMessage);
    this.appLogger.logError(errorMessage, {context: "AppInitService.handleError"});
  }

  private isOidcCallback(): boolean {
    const urlParams = new URLSearchParams(this.window.location.search);
    const state = urlParams.get('state');
    const code = urlParams.get('code');

    if (!code || !state) return false;

    const isJWT = code.split('.').length === 3;

    // Check if state looks like OIDC (typically longer, base64-like)
    // and no EHR-specific parameters (iss, launch)
    const hasEhrParams = urlParams.has('iss') || urlParams.has('launch');
    const hasOidcLikeState = state.length > 20 && !hasEhrParams;


    // Not relying on JWT style code to detect EHR,
    // and using ehr_ prefix in State
    const isEhrFlow =
      state.startsWith('ehr_') ||
      hasEhrParams;

    // Replaced "result" with isOidcCallback variable
    const isOidcCallback = !isEhrFlow;
    return isOidcCallback;
  }
}
